import { zodResolver } from '@hookform/resolvers/zod';
import { ClipboardList, Info, MessageSquare, Paperclip, Truck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ErrorState } from '@/ui/ErrorState';
import { FormPage } from '@/ui/FormPage';
import { FormSection } from '@/ui/FormSection';
import { PageHeader } from '@/ui/PageHeader';
import { EmptyState } from '@/ui/EmptyState';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/base/form';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Skeleton } from '@/ui/base/skeleton';
import { Textarea } from '@/ui/base/textarea';

import { RECURSO_DO_TIPO, TIPOS_DE_SOLICITACAO, type TipoDeSolicitacao } from './contrato';
import { useRecursosDisponiveis } from './dados';

/**
 * `tipo` fica como `z.string()` (não `z.enum`) só para o valor inicial poder
 * ser `''` sem forçar `undefined` no tipo de `ValoresDoFormulario` — o Select
 * não tem "nenhum selecionado" fora de string vazia. A validação de pertencer
 * ao conjunto de tipos vem do próprio `<Select>`, que só oferece as opções
 * de `TIPOS_DE_SOLICITACAO`.
 */
const esquema = z
  .object({
    tipo: z.string().min(1, 'Selecione o tipo de solicitação'),
    recurso_afetado_id: z.string().optional(),
    motivo: z
      .string()
      .trim()
      .min(10, 'Descreva o motivo com pelo menos 10 caracteres')
      .max(1000, 'Motivo deve ter no máximo 1000 caracteres'),
  })
  .superRefine((valores, ctx) => {
    if (RECURSO_DO_TIPO[valores.tipo as TipoDeSolicitacao] && !valores.recurso_afetado_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurso_afetado_id'],
        message: 'Selecione o recurso afetado',
      });
    }
  });

type ValoresDoFormulario = z.infer<typeof esquema>;

/**
 * `/credenciado/solicitacoes/nova` — abertura de uma solicitação de serviço.
 *
 * NÃO GRAVA. Não há API de solicitações de serviço: "Enviar solicitação"
 * valida o formulário e mostra o aviso de indisponibilidade, sem limpar o
 * formulário nem navegar — voltar à lista seria fingir que a solicitação
 * existe. Um aviso fixo acompanha o botão, porque aviso passageiro some e a
 * captura de tela fica parecendo envio. Mesmo padrão de `SlaAprovacaoSmePage`
 * e de `financeiro/solicitacoes-sme/NovaSolicitacaoSmeModal.tsx`; ver
 * `dados.ts` e o README do módulo.
 */
export function NovaSolicitacaoPage() {
  const [searchParams] = useSearchParams();

  const recursos = useRecursosDisponiveis();

  /**
   * Atalhos de outras telas (ex.: "Solicitar substituição" em Condutores)
   * chegam aqui com `?tipo=substituicao_condutor` já preenchido — só aceita
   * um valor que exista em `TIPOS_DE_SOLICITACAO`, para não deixar o Select
   * num estado que a lista de opções não reconhece.
   */
  const tipoPreSelecionado = TIPOS_DE_SOLICITACAO.some(
    (opcao) => opcao.value === searchParams.get('tipo'),
  )
    ? (searchParams.get('tipo') as TipoDeSolicitacao)
    : '';

  const form = useForm<ValoresDoFormulario>({
    resolver: zodResolver(esquema),
    defaultValues: { tipo: tipoPreSelecionado, recurso_afetado_id: '', motivo: '' },
    mode: 'onSubmit',
  });

  const tipoSelecionado = (form.watch('tipo') || undefined) as TipoDeSolicitacao | undefined;
  const tipoDeRecursoExigido = tipoSelecionado ? RECURSO_DO_TIPO[tipoSelecionado] : undefined;
  const recursosDoTipo = (recursos.data ?? []).filter((item) => item.tipo === tipoDeRecursoExigido);

  // Só chega aqui com o formulário válido (`form.handleSubmit`). Não há onde
  // gravar: avisa, e o que foi preenchido fica na tela.
  function enviar() {
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Nova solicitação"
        description="Abra um pedido de serviço junto ao DTP — substituição de veículo, condutor ou monitor, alteração cadastral ou outro assunto."
      />

      {recursos.isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar os recursos disponíveis agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void recursos.refetch() }}
        />
      ) : (
        <Form {...form}>
          <FormPage
            onSubmit={form.handleSubmit(enviar)}
            submitLabel="Enviar solicitação"
            backTo="/credenciado/solicitacoes"
            maxWidth="max-w-3xl"
          >
            <FormSection
              title="Tipo de solicitação"
              subtitle="O que você precisa pedir ao DTP"
              icon={ClipboardList}
            >
              <FormField
                control={form.control}
                name="tipo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select
                      value={field.value ?? ''}
                      onValueChange={(valor) => {
                        field.onChange(valor);
                        form.setValue('recurso_afetado_id', '');
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo…" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TIPOS_DE_SOLICITACAO.map((opcao) => (
                          <SelectItem key={opcao.value} value={opcao.value}>
                            {opcao.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            {tipoDeRecursoExigido && (
              <FormSection
                title="Recurso afetado"
                subtitle="Qual veículo, condutor ou monitor esta solicitação envolve"
                icon={Truck}
              >
                {recursos.isLoading ? (
                  <div role="status" aria-live="polite" className="space-y-2">
                    <Skeleton className="h-11 w-full" />
                    <p className="sr-only">Carregando recursos disponíveis.</p>
                  </div>
                ) : recursosDoTipo.length === 0 ? (
                  <EmptyState
                    variant="search"
                    title="Nenhum recurso cadastrado desse tipo"
                    description="Cadastre o recurso antes de abrir esta solicitação."
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="recurso_afetado_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Recurso</FormLabel>
                        <Select value={field.value ?? ''} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o recurso…" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {recursosDoTipo.map((opcao) => (
                              <SelectItem key={opcao.id} value={opcao.id}>
                                {opcao.rotulo}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </FormSection>
            )}

            <FormSection
              title="Motivo"
              subtitle="Explique o que está acontecendo e o que você precisa"
              icon={MessageSquare}
            >
              <FormField
                control={form.control}
                name="motivo"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={5}
                        maxLength={1000}
                        placeholder="Descreva o motivo da solicitação…"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection
              title="Anexo"
              subtitle="Opcional — documento que ajude a análise"
              icon={Paperclip}
            >
              <div className="space-y-2">
                <Label htmlFor="anexo-da-solicitacao">Arquivo</Label>
                <Input id="anexo-da-solicitacao" type="file" />
              </div>
            </FormSection>

            <Alert>
              <Info className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                O envio de solicitações ainda não está disponível. Você pode preencher e conferir o
                formulário, mas nada é registrado.
              </AlertDescription>
            </Alert>
          </FormPage>
        </Form>
      )}
    </div>
  );
}

export default NovaSolicitacaoPage;
